import React, { useState, useEffect } from "react";
import { Form, Input, Select, InputNumber, Space, Typography, Button, Row, Col, Divider, Checkbox, Alert } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getStreets } from "../../../api/streets/streetRequest";
import { getLinesByStreetId } from "../../../api/lines/linesRequest";
import streetIcon from "../../../assets/icons/street.png";
import undergroundIcon from "../../../assets/icons/underground.png";
import coveredIcon from "../../../assets/icons/covered.png";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const parkingTypeIcons = {
  street: streetIcon,
  underground: undergroundIcon,
  "enclosed above-ground": coveredIcon,
};

const parkingTypeLabels = {
  street: "Parking voirie",
  underground: "Parking souterrain",
  "enclosed above-ground": "Parking aérien fermé",
};

export const ParkingForm = ({ form, onFinish, editingParking, hasMapData, currentParking }) => {
  const [streets, setStreets] = useState([]);
  const [loadingStreets, setLoadingStreets] = useState(false);
  const [streetLines, setStreetLines] = useState([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const selectedStreetId = Form.useWatch("external_id", form);
  const selectedParkingType = Form.useWatch("parking_type", form);
  const savePolygon = Form.useWatch("save_polygon", form);
  const saveMarker = Form.useWatch("save_marker", form);

  // Check if we have polygon and marker data available
  const hasPolygonData = currentParking?.coordinates && currentParking.coordinates.length > 0;
  const hasMarkerData = !!currentParking?.marker;

  useEffect(() => {
    fetchStreets();
  }, []);

  useEffect(() => {
    // Clear street and lines when parking type changes away from "street"
    if (selectedParkingType && selectedParkingType !== "street") {
      form.setFieldsValue({ 
        external_id: undefined, 
        lines: [] 
      });
      setStreetLines([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParkingType]);

  useEffect(() => {
    if (selectedStreetId) {
      fetchStreetLines(selectedStreetId);
      // Clear existing lines when street changes
      form.setFieldsValue({ lines: [] });
    } else {
      setStreetLines([]);
      form.setFieldsValue({ lines: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStreetId]);

  const fetchStreets = async () => {
    try {
      setLoadingStreets(true);
      const data = await getStreets();
      setStreets(data);
    } catch (error) {
      console.error("Error fetching streets:", error);
    } finally {
      setLoadingStreets(false);
    }
  };

  const fetchStreetLines = async (streetId) => {
    try {
      setLoadingLines(true);
      const data = await getLinesByStreetId(streetId);
      setStreetLines(data || []);
    } catch (error) {
      console.error("Error fetching street lines:", error);
      setStreetLines([]);
    } finally {
      setLoadingLines(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        name="name"
        label="Nom du parking"
        rules={[{ required: true, message: "Le nom est requis" }]}
      >
        <Input placeholder="Nom du parking" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="name_ar"
            label="Nom en arabe"
          >
            <Input placeholder="الاسم بالعربية" dir="rtl" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="name_fr"
            label="Nom en français"
          >
            <Input placeholder="Nom en français" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="parking_type"
        label="Type de parking"
        rules={[{ required: true, message: "Le type est requis" }]}
      >
        <Select placeholder="Sélectionner le type de parking">
          {Object.keys(parkingTypeIcons).map((type) => (
            <Option key={type} value={type}>
              <Space>
                <img
                  src={parkingTypeIcons[type]}
                  alt={parkingTypeLabels[type]}
                  style={{ width: 20, height: 20, objectFit: "contain" }}
                />
                {parkingTypeLabels[type]}
              </Space>
            </Option>
          ))}
        </Select>
      </Form.Item>

      {(hasPolygonData || hasMarkerData) && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            background: "#e6f7ff",
            borderRadius: 8,
            border: "1px solid #91d5ff",
          }}
        >
          <Text strong style={{ display: "block", marginBottom: 12 }}>
            Options de sauvegarde de la carte
          </Text>
          <Space direction="vertical">
            {hasPolygonData && (
              <Form.Item
                name="save_polygon"
                valuePropName="checked"
                initialValue={true}
                style={{ marginBottom: 0 }}
                rules={[
                  {
                    validator: (_, value) => {
                      const markerValue = form.getFieldValue("save_marker");
                      if (!value && !markerValue) {
                        return Promise.reject(
                          new Error("Au moins une option doit être sélectionnée")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Checkbox>
                  <Space>
                    <span>Sauvegarder le polygone</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      (Zone délimitée sur la carte)
                    </Text>
                  </Space>
                </Checkbox>
              </Form.Item>
            )}
            {hasMarkerData && (
              <Form.Item
                name="save_marker"
                valuePropName="checked"
                initialValue={true}
                style={{ marginBottom: 0 }}
                rules={[
                  {
                    validator: (_, value) => {
                      const polygonValue = form.getFieldValue("save_polygon");
                      if (!value && !polygonValue) {
                        return Promise.reject(
                          new Error("Au moins une option doit être sélectionnée")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Checkbox>
                  <Space>
                    <span>Sauvegarder le marqueur</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      (Point d'intérêt sur la carte)
                    </Text>
                  </Space>
                </Checkbox>
              </Form.Item>
            )}
          </Space>
          {!savePolygon && !saveMarker && (hasPolygonData || hasMarkerData) && (
            <Alert
              message="Au moins une option doit être sélectionnée"
              type="error"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
        </div>
      )}

      {selectedParkingType === "street" && (
        <>
          <Form.Item
            name="external_id"
            label="Rue associée"
            rules={[
              { 
                required: selectedParkingType === "street", 
                message: "La rue est requise pour les parkings de voirie" 
              }
            ]}
          >
            <Select
              placeholder="Sélectionner une rue"
              loading={loadingStreets}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {streets.map((street) => (
                <Option key={street.id} value={street.id}>
                  {street.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </>
      )}


      <div style={{ marginBottom: 16 }}>
        <Text strong>Tarifs (personnalisables)</Text>
        <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
          Ajouter des tarifs avec leur période ou durée
        </Text>
      </div>

      <Form.List
        name="tariff_info"
        rules={[
          {
            validator: async (_, tariffs) => {
              if (!tariffs || tariffs.length === 0) {
                return Promise.reject(new Error("Au moins un tarif est requis"));
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map((field, index) => (
              <Space
                key={field.key}
                style={{ display: "flex", marginBottom: 8, alignItems: "center", width: "100%" }}
                align="baseline"
              >
                <Form.Item
                  {...field}
                  name={[field.name, "key"]}
                  rules={[
                    { required: true, message: "Période/durée requise" },
                  ]}
                  style={{ marginBottom: 0, width: 200 }}
                >
                  <Input placeholder="Ex: 1h, 07-14, journée" />
                </Form.Item>
                <Form.Item
                  {...field}
                  name={[field.name, "value"]}
                  rules={[
                    { required: true, message: "Prix requis" },
                    {
                      validator: (_, value) => {
                        if (value && value > 0) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Le prix doit être positif"));
                      },
                    },
                  ]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <InputNumber
                    min={0}
                    step={0.5}
                    style={{ width: "100%" }}
                    placeholder="Prix"
                    addonAfter="MAD"
                  />
                </Form.Item>
                {fields.length > 1 && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                )}
              </Space>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Ajouter un tarif
              </Button>
              <Form.ErrorList errors={errors} />
            </Form.Item>
          </>
        )}
      </Form.List>

      {selectedParkingType === "street" && (
        <>
          <Divider />

          <div style={{ marginBottom: 16 }}>
            <Text strong>Lignes de parking</Text>
            <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              Ajouter les lignes associées à ce parking (sélectionnez d'abord une rue)
            </Text>
          </div>

          <Form.List name="lines">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <Space
                key={field.key}
                style={{ display: "flex", marginBottom: 8, width: "100%" }}
                align="baseline"
              >
                <Form.Item
                  {...field}
                  name={[field.name, "external_id"]}
                  rules={[
                    { required: true, message: "La ligne est requise" },
                  ]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Select
                    placeholder="Sélectionner une ligne de rue"
                    loading={loadingLines}
                    disabled={!selectedStreetId || streetLines.length === 0}
                    showSearch
                    optionFilterProp="children"
                  >
                    {streetLines.map((line) => (
                      <Option key={line.id} value={line.id}>
                        {line.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  {...field}
                  name={[field.name, "name"]}
                  rules={[
                    { required: true, message: "Nom requis" },
                  ]}
                  style={{ marginBottom: 0, flex: 1 }}
                >
                  <Input placeholder="Nom de la ligne" />
                </Form.Item>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => remove(field.name)}
                />
              </Space>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
                disabled={!selectedStreetId || streetLines.length === 0}
              >
                Ajouter une ligne
              </Button>
              {!selectedStreetId && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Veuillez d'abord sélectionner une rue
                </Text>
              )}
              {selectedStreetId && streetLines.length === 0 && (
                <Text type="warning" style={{ fontSize: 12 }}>
                  Aucune ligne disponible pour cette rue
                </Text>
              )}
            </Form.Item>
          </>
        )}
      </Form.List>
        </>
      )}

      <Divider />

      <Form.Item 
        name="archived" 
        valuePropName="checked"
        initialValue={false}
      >
        <Checkbox>
          <Space>
            <span>Archiver ce parking</span>
            <Text type="secondary" style={{ fontSize: 12 }}>
              (Le parking sera marqué comme archivé)
            </Text>
          </Space>
        </Checkbox>
      </Form.Item>

      <Form.Item name="notes" label="Notes">
        <TextArea rows={3} placeholder="Notes supplémentaires..." />
      </Form.Item>
    </Form>
  );
};
